import { createAction } from '@reduxjs/toolkit'

import { BotTemplateApi } from 'shared/api/requests/botTemplate'
import { StoreTemplateApi } from 'shared/api/requests/storeTemplate'
import { createAsyncAction } from 'shared/lib/redux'
import { analyticsService } from 'utils/services/analytics'

const isStoreId = (id: string): boolean => !isNaN(Number(id))

interface TemplateInstallationConflicts {
  wm: boolean
  dr: boolean
  mm: boolean
  gtext: boolean
  igdr: boolean
  sm: boolean
  ib: boolean
  igib: boolean
  igmm: boolean
}

export const resolveConflict = createAction<TemplateInstallationConflicts>(
  'templateInstallation/resolveConflict',
)

export const resetInstallation = createAction('templateInstallation/resetInstallation')

export const fetchBotTemplate = createAsyncAction(
  'templateInstallation/fetchBotTemplate',
  async (templateHash: string) => {
    if (isStoreId(templateHash)) {
      const response = await StoreTemplateApi.getStoreTemplate({
        query: { template_id: templateHash },
      })
      const item = response.data
      return {
        template_data: {
          ...item.template,
          current_version: item.template.internal_template_data.current_version,
        },
        user_info: { name: item.template.authors },
      }
    }
    const response = await BotTemplateApi.getBotTemplate({ path: { template_hash: templateHash } })

    return response.data
  },

  {
    autoHandleByGuards: [
      BotTemplateApi.getBotTemplate.isInvalidLinkError,
      StoreTemplateApi.getStoreTemplate.isInvalidLinkError,
    ],
  },
)

export const installBotTemplate = createAsyncAction(
  'templateInstallation/install',
  async (
    { id, pageId, signature }: { id: string; pageId: string; signature: string },
    { getState },
  ) => {
    const state = getState()
    const { customEventsMap, tagsMap, cufsMap, gafsMap, otnsMap } = state.targetPage
    const { generalElementsConflicts: conflicts, settings } = state.templates.installation
    const isStoreTemplate = isStoreId(id)

    const body = {
      page_id: pageId.replace('fb', ''),
      replace_welcome_message: conflicts.wm,
      replace_default_reply: conflicts.dr,
      replace_main_menu: conflicts.mm,
      replace_greeting_text: conflicts.gtext,
      replace_ig_default_reply: conflicts.igdr,
      replace_story_mention_reply: conflicts.sm,
      replace_ice_breakers: conflicts.ib,
      replace_ig_ice_breakers: conflicts.igib,
      replace_ig_main_menu: conflicts.igmm,
      mapping_settings: {
        custom_fields: cufsMap,
        tags: tagsMap,
        global_fields: gafsMap,
        otn_reasons: otnsMap,
        custom_events: customEventsMap,
      },
      fill_fields: settings,
      async: signature,
    }

    if (isStoreTemplate) {
      const response = await StoreTemplateApi.installStoreTemplate({
        path: { template_id: Number(id) },
        body,
      })

      analyticsService.sendEvent('TEMPLATE_STORE.INSTALL_TEMPLATE', {
        template_id: id,
      })

      return response.data
    }

    const response = await BotTemplateApi.installTemplate({
      path: { template_hash: id },
      body,
    })
    return response.data
  },
  {
    autoHandleByGuards: [
      BotTemplateApi.installTemplate.isInvalidLinkError,
      BotTemplateApi.installTemplate.isBlankPageError,
      StoreTemplateApi.installStoreTemplate.isInvalidLinkError,
    ],
  },
)
